import React from 'react'
import PropTypes from 'prop-types'
import { Card, Grid, InputNumber, Space, Table, Tag, Typography } from 'antd'
import {
  SLOT_LABELS,
  TABLES,
  formatRate,
  percentChange,
  rateLabel,
  toUsdLabel
} from './utils'

const { Text } = Typography

// One row per currency, read-only or as inputs. At phone width each currency is a card.
const RatesTable = ({ table, rates, editing, edits, errors, onEdit }) => {
  const screens = Grid.useBreakpoint()
  const config = TABLES[table]
  const currencies = rates?.currencies || []

  const readValue = (currency, slot) => {
    const value = currency[slot]
    if (slot === 'ghsRate') return rateLabel(currency.code, value)
    if (slot === 'toUsd') return toUsdLabel(table, currency.code, value)
    return Number(value) > 0 ? formatRate(value) : '—'
  }

  const cell = (currency, slot) => {
    const field = currency.fields?.[slot]
    if (!field) return <Text type="secondary">—</Text>
    if (!editing) return readValue(currency, slot)

    const value = field in edits ? edits[field] : Number(currency[slot]) || 0
    const change = field in edits ? percentChange(currency[slot], edits[field]) : null
    return (
      <Space direction="vertical" size={2}>
        <InputNumber
          value={value}
          min={0}
          step={0.01}
          stringMode
          style={{ width: 140 }}
          status={errors[field] ? 'error' : undefined}
          onChange={next => onEdit(field, next)}
          aria-label={`${currency.code} ${SLOT_LABELS[slot]}`}
        />
        {change !== null && change !== 0 ? (
          <Text type={Math.abs(change) > 10 ? 'warning' : 'secondary'} style={{ fontSize: 12 }}>
            was {formatRate(currency[slot])} ({change > 0 ? '+' : ''}
            {change}%)
          </Text>
        ) : null}
        {errors[field] ? (
          <Text type="danger" style={{ fontSize: 12 }}>
            {errors[field]}
          </Text>
        ) : null}
      </Space>
    )
  }

  const currencyCell = currency => (
    <Space direction="vertical" size={2}>
      <Text strong>{currency.code}</Text>
      {currency.unset?.length ? <Tag color="orange">{config.unset(currency.code)}</Tag> : null}
    </Space>
  )

  if (!screens.md) {
    return (
      <Space direction="vertical" style={{ width: '100%' }}>
        {currencies.map(currency => (
          <Card key={currency.code} size="small" title={currencyCell(currency)}>
            {config.slots
              .filter(slot => currency.fields?.[slot])
              .map(slot => (
                <div key={slot} style={{ marginBottom: 8 }}>
                  <Text type="secondary">{SLOT_LABELS[slot]}</Text>
                  <div>{cell(currency, slot)}</div>
                </div>
              ))}
          </Card>
        ))}
      </Space>
    )
  }

  /* eslint-disable react/display-name */
  const columns = [
    { title: 'Currency', key: 'code', width: 200, render: (_, r) => currencyCell(r) },
    ...config.slots.map(slot => ({
      title: SLOT_LABELS[slot],
      key: slot,
      render: (_, r) => cell(r, slot)
    }))
  ]
  /* eslint-enable react/display-name */

  return (
    <Table rowKey="code" dataSource={currencies} columns={columns} pagination={false} />
  )
}

RatesTable.propTypes = {
  table: PropTypes.oneOf(['banking', 'car']).isRequired,
  rates: PropTypes.object,
  editing: PropTypes.bool,
  edits: PropTypes.object,
  errors: PropTypes.object,
  onEdit: PropTypes.func
}

RatesTable.defaultProps = {
  editing: false,
  edits: {},
  errors: {},
  onEdit: () => {}
}

export default RatesTable
